import React, { useEffect, useState } from "react";
import { useMutation } from "react-query";
import UserService from "../../services/userService";
import { useTranslation } from "react-i18next";

const ChangePassword: React.FC = () => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [renewPassword, setRenewPassword] = useState("");
  const { t } = useTranslation();
  // Separate state for each password visibility toggle
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showRenewPassword, setShowRenewPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState({
    currentPassword: "",
  });

  const [showSuccess, setShowSuccess] = useState(false);
  useEffect(() => {
    if (showSuccess) {
      const inv = setTimeout(() => {
        setShowSuccess(false);
      }, 3000); // Show success message for 3 seconds before hiding it.
      return () => clearTimeout(inv); // Clear timeout when component unmounts.
    }
  }, [showSuccess]);

  const mutation = useMutation(
    async () => {
      return await UserService.updatePassword(currentPassword, newPassword);
    },
    {
      onSuccess: (data) => {
        setShowSuccess(true);
      },
      onError: (error: any) => {
        if (error.code === 1021) {
          setValidationErrors({
            ...validationErrors,
            currentPassword: t("The current password is incorrect"),
          });
        }
        console.error("Error updating user info:", error);
      },
    }
  );

  const handleUpdate = () => {
    mutation.mutate();
  };
  return (
    <div className="w-[740px] h-[1264px] rounded-[1.25rem] py-[30px] flex flex-col gap-[30px]">
      <div className="flex justify-between">
        <div className="flex items-center gap-2">
          <h1 className="font-sans text-[24px] font-semibold leading-[40px] text-left decoration-skip-ink-auto">
            {t("Change password")}
          </h1>
        </div>
      </div>
      <div className="border border-[#DCDCDC] p-[20px] pt-[1.3125rem] rounded-[1.25rem] flex flex-col gap-[20px]">
        {/* Current Password */}
        <div className="flex flex-col gap-[0.625rem]">
          <p className="text-[#232323]">{t("Current Password")}</p>
          <div className="relative w-full">
            <input
              type={showCurrentPassword ? "text" : "password"}
              id="currentPassword"
              name="currentPassword"
              placeholder={t("Password")}
              className="border p-2 w-full text-[0.875rem] leading-[1.1919rem] rounded-[0.625rem] h-[3.125rem] focus:outline-none"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
            <button
              type="button"
              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5"
            >
              <img
                src={`../../assets/images/eye${
                  showCurrentPassword ? "-slash" : ""
                }.svg`}
                alt="toggle password visibility"
              />
            </button>
          </div>
          {validationErrors.currentPassword && (
            <div className="text-[#DA3838] text-[12px]">
              {validationErrors.currentPassword}
            </div>
          )}
        </div>

        {/* New Password */}
        <div className="flex flex-col gap-[0.625rem]">
          <p className="text-[#232323]">{t("New Password")}</p>
          <div className="relative w-full">
            <input
              type={showNewPassword ? "text" : "password"}
              id="newPassword"
              name="newPassword"
              placeholder={t("Password")}
              className="border p-2 w-full text-[0.875rem] leading-[1.1919rem] rounded-[0.625rem] h-[3.125rem] focus:outline-none"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <button
              type="button"
              onClick={() => setShowNewPassword(!showNewPassword)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5"
            >
              <img
                src={`../../assets/images/eye${
                  showNewPassword ? "-slash" : ""
                }.svg`}
                alt="toggle password visibility"
              />
            </button>
          </div>
          <p className="text-[#5E6D77] text-[12px]">
            {t("Minimum 6 characters")}
          </p>
        </div>

        {/* Re-enter New Password */}
        <div className="flex flex-col gap-[0.625rem]">
          <p className="text-[#232323]">{t("Re-enter New Password")}</p>
          <div className="relative w-full">
            <input
              type={showRenewPassword ? "text" : "password"}
              id="renewPassword"
              name="renewPassword"
              placeholder={t("Password")}
              className="border p-2 w-full text-[0.875rem] leading-[1.1919rem] rounded-[0.625rem] h-[3.125rem] focus:outline-none"
              value={renewPassword}
              onChange={(e) => setRenewPassword(e.target.value)}
            />
            <button
              type="button"
              onClick={() => setShowRenewPassword(!showRenewPassword)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5"
            >
              <img
                src={`../../assets/images/eye${
                  showRenewPassword ? "-slash" : ""
                }.svg`}
                alt="toggle password visibility"
              />
            </button>
          </div>
          {newPassword !== renewPassword && (
            <p className="text-[#DA3838] text-[0.75rem]">
              {t("Password doesn’t match")}
            </p>
          )}
          {showSuccess && (
            <p className="text-[#007AFF] text-[0.75rem]">
              {t("Update successfully")}
            </p>
          )}
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            type="button"
            disabled={
              !currentPassword ||
              !newPassword ||
              newPassword !== renewPassword ||
              newPassword.length < 6
            }
            onClick={handleUpdate}
            className={`w-[7.8125rem] py-[15px] rounded-[50px] border border-[#232323] ${
              currentPassword &&
              newPassword &&
              newPassword === renewPassword &&
              newPassword.length >= 6
                ? "bg-black text-white"
                : "cursor-not-allowed bg-black text-white"
            } text-center focus:ring-gray-200 font-noto-sans text-base font-semibold leading-5 decoration-none`}
          >
            {t("Save")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
